import { GraphQLAiRepository } from "@app/repositories/GraphQLRepositories/ai";
import {
    makeApiUrl,
    type MessageType,
    useChatWithAuthHeaders,
} from "@app/utils/ai/chat";
import { type ScoreValue } from "@generated/client/graphql";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { agentRoutes } from "../services";

export const apiUrls = {
    cdp_expert: makeApiUrl(agentRoutes.cdp_expert),
    evs_expert: makeApiUrl(agentRoutes.evs_expert),
    doc_reviewer: makeApiUrl(agentRoutes.doc_reviewer),
    quick_answer: makeApiUrl(agentRoutes.quick_answer_suggestor),
    /**
     * this isn't a chat agent, it's a title generator
     * it's a special case because it's not a chat agent
     * so there is no associated agent route.
     * */
    title_generator: makeApiUrl("titleGenerator"),
};

const aiRepository = new GraphQLAiRepository();

export const useCdpExpertChat = () => {
    const api = apiUrls.cdp_expert;
    return useChatWithAuthHeaders({
        api,
    });
};

export const useEvsExpertChat = () => {
    const api = apiUrls.evs_expert;
    return useChatWithAuthHeaders({
        api,
    });
};

export const useDocumentReviewerChat = () => {
    const api = apiUrls.doc_reviewer;
    return useChatWithAuthHeaders({
        sendExtraMessageFields: true,
        api,
    });
};

export const useQuickAnswerChat = () => {
    const api = apiUrls.quick_answer;
    return useChatWithAuthHeaders({
        api,
    });
};

export const useScoreResponse = () => {
    const { mutate: scoreResponse, ...mutation } = useMutation({
        mutationFn: ({
            traceId,
            scoreValue,
        }: {
            traceId: string;
            scoreValue: ScoreValue;
        }) => aiRepository.scoreResponse(traceId, scoreValue),
    });

    return { scoreResponse, ...mutation };
};

export const useTitleGenerator = (
    /**
     * The messages to send to the title generator
     */
    messages: MessageType[] = [],

    /**
     * The status of the messages list to generate a title
     */
    status: "submitted" | "streaming" | "ready" | "error" = "ready",

    /**
     * Exact number of messages required to generate a title
     */
    messageThreshold: number,
) => {
    const api = apiUrls.title_generator;

    const { append, messages: generatedMessages } = useChatWithAuthHeaders({
        api,
    });
    const shouldGenerate =
        messages?.length === messageThreshold && status === "ready";

    const title = useMemo(() => {
        if (generatedMessages?.length !== 2) {
            return undefined;
        }

        return generatedMessages?.[1]?.content;
    }, [generatedMessages]);

    useEffect(() => {
        if (shouldGenerate) {
            append({
                id: "title-generator-user-message",
                role: "user",
                content: JSON.stringify(messages ?? []),
            });
        }
    }, [shouldGenerate]);

    return title;
};
